import { useCallback, useEffect, useId, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { NudgeBanner } from "../components/NudgeBanner";
import { SupportPanel } from "../components/support/SupportPanel";
import { BackToTop, Icon, SkipLink, type IconName } from "../components/ui";
import { useHeightVar } from "../components/ui/sticky";
import { LMS_VERSION, vtag, type RuntimeConfig } from "../config";
import { Logo, useBranding } from "../lib/branding";
import { RouteFocus } from "./RouteFocus";

const NAV_KEY = "lms.nav";
const WIDE = "(min-width: 768px)";

/** The person's choice for wide screens, cached per device; read synchronously so the first frame is already right. */
function readCollapsed(): boolean {
  try {
    return localStorage.getItem(NAV_KEY) === "collapsed";
  } catch {
    return false;
  }
}

function useWide(): boolean {
  const [wide, setWide] = useState(() => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(WIDE).matches : true));
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia(WIDE);
    const onChange = () => setWide(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return wide;
}

/**
 * The shell (design.md §10): skip link first, then the rail — the banner,
 * holding the product name, the one main navigation and the person — then
 * <main id="main"> that the skip link and RouteFocus land on, and a footer
 * with the build's version beside a link to its release notes.
 *
 * The rail collapses to an icon strip at the far left and pops back out
 * (owner, 2026-09-21; gairm/lms#211). Wide screens: expanded by default,
 * the choice kept per device, the content moves over. Narrow screens: the
 * strip by default — always in the document, so every link keeps its
 * name — and expanding overlays the page; Escape or a tap outside closes
 * it, and a navigation closes it. Areas render from the matrix's
 * permissions, never from a role name (permission-matrix.md §6).
 */
export function AppLayout({ config }: { config: RuntimeConfig }) {
  const { status, me, signOut } = useAuth();
  const { pathname, hash } = useLocation();
  const wide = useWide();
  const [collapsedPref, setCollapsedPref] = useState(readCollapsed);
  // The path it was opened on: a navigation closes a narrow-screen overlay by making this stale, no effect needed.
  const [openedAt, setOpenedAt] = useState<string | null>(null);
  const openNarrow = openedAt === pathname;
  const setOpenNarrow = useCallback((open: boolean) => setOpenedAt(open ? pathname : null), [pathname]);
  const navId = useId();
  const version = vtag(LMS_VERSION);
  const apiVersion = me?.version;
  const nonProd = config.envName !== "prod";
  // The tenant's own name and mark (TEN-04, TEN-07): from /v1/me once it has answered, else config.json's per-host value.
  const branding = useBranding();
  const productName = branding.productName;

  const collapsed = wide ? collapsedPref : !openNarrow;
  const toggle = useCallback(() => {
    if (wide) {
      setCollapsedPref((c) => {
        try {
          localStorage.setItem(NAV_KEY, c ? "expanded" : "collapsed");
        } catch {
          // no storage: the choice lasts the visit
        }
        return !c;
      });
    } else {
      setOpenNarrow(!openNarrow);
    }
  }, [wide, openNarrow, setOpenNarrow]);

  // The round ? (PLT-09): the panel opens over the page, from the button or the `?` key — outside a field, without a
  // modifier, and not while another dialog has the page.
  const [supportOpen, setSupportOpen] = useState(false);
  const closeSupport = useCallback(() => setSupportOpen(false), []);
  // Where focus goes when the panel closes, whether it was opened by a click here or by the ? key from anywhere.
  const helpButton = useRef<HTMLButtonElement>(null);
  const signedIn = status === "signed-in";
  // The ? row sticks at the top of the content column, so help is in reach however far a page scrolls; its height is
  // --shell-bar, which the page banner and a table's bands stick under (sticky.ts; Claude's walk of 2026-09-23).
  const helpRow = useRef<HTMLElement>(null);
  useHeightVar(helpRow, "shell-bar", signedIn);
  useEffect(() => {
    if (!signedIn) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "?" || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName))) return;
      if (document.querySelector("dialog[open]")) return;
      e.preventDefault();
      setSupportOpen(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [signedIn]);

  // A navigation opens the next page at its top: the document keeps its scroll offset across a client-side route
  // change, so a page opened from a scrolled one arrived already scrolled, its banner stuck over rows above it
  // (gairm/lms#215; the template's pitfall 1). Not on the first render — a reload keeps the browser's own
  // restoration — and not for a #hash, which has a target of its own (the footer's "What changed in").
  const firstPath = useRef(true);
  useEffect(() => {
    if (firstPath.current) {
      firstPath.current = false;
      return;
    }
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);

  // A narrow-screen overlay closes on Escape.
  useEffect(() => {
    if (wide || !openNarrow) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenNarrow(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [wide, openNarrow, setOpenNarrow]);

  const links: Array<{ to: string; label: string; icon: IconName; end: boolean }> = [
    { to: "/", label: "Home", icon: "home", end: true },
    ...(me?.permissions.includes("my_training.read") ? [{ to: "/training", label: "My training", icon: "graduation-cap" as const, end: false }] : []),
    ...(me?.permissions.includes("user.list") ? [{ to: "/program/users", label: "Users", icon: "users" as const, end: false }] : []),
    ...(me?.permissions.includes("assignment.create") ? [{ to: "/program/assignments", label: "Assignments", icon: "clipboard-list" as const, end: false }] : []),
    ...(me?.permissions.includes("audit_event.list") ? [{ to: "/program/audit", label: "Audit trail", icon: "list" as const, end: false }] : []),
    ...(status === "signed-in" ? [{ to: "/account", label: "Account", icon: "user" as const, end: false }] : []),
    { to: "/docs", label: "Documentation", icon: "book", end: false },
    ...(nonProd ? [{ to: "/primitives", label: "Primitives", icon: "grid" as const, end: false }] : []),
  ];

  // Collapsed, the label is the name for a screen reader and a tooltip for everyone else; expanded, it is the text.
  const label = (text: string) =>
    collapsed ? (
      <>
        <span className="sr-only">{text}</span>
        <Tip text={text} />
      </>
    ) : (
      <span className="min-w-0 truncate">{text}</span>
    );
  const item = "group relative flex min-h-11 w-full items-center gap-3 rounded-lg px-3 no-underline";

  return (
    <div className="min-h-dvh md:grid md:grid-cols-[auto_minmax(0,1fr)]">
      <SkipLink />
      <RouteFocus productName={productName} />
      {!wide && openNarrow && <div aria-hidden="true" className="fixed inset-0 z-30 bg-black/40" onClick={() => setOpenNarrow(false)} />}
      <header
        data-testid="rail"
        data-collapsed={collapsed ? "true" : "false"}
        className={`fixed inset-y-0 left-0 z-40 flex h-dvh flex-col border-r border-line bg-surface motion-safe:transition-[width] motion-safe:duration-200 md:sticky md:top-0 ${collapsed ? "w-14" : "w-60 shadow-xl md:shadow-none"}`}
      >
        {/* Two rows of the same height in both states — the logo, then the toggle — so the toggle sits at the same place on
            the screen whether the rail is open or collapsed (owner, 2026-09-21). Open: the full logo, the tenant's mark
            with its name beside it; collapsed: the mark alone, the abbreviated logo, with the name as a tooltip. */}
        <div className="px-2 pt-2">
          <NavLink to="/" className={`${item} ${collapsed ? "justify-center px-0" : ""}`}>
            <Logo branding={branding} abbreviated={collapsed} />
            {collapsed && <Tip text={productName} />}
          </NavLink>
          <div className={`flex ${collapsed ? "justify-center" : "justify-end"}`}>
            <button type="button" aria-expanded={!collapsed} aria-controls={navId} onClick={toggle} className="group relative flex min-h-11 w-11 items-center justify-center rounded-lg text-ink hover:bg-canvas">
              <Icon name={collapsed ? "chevrons-right" : "chevrons-left"} />
              <span className="sr-only">Menu</span>
              <Tip text={collapsed ? "Expand the menu" : "Collapse the menu"} />
            </button>
          </div>
        </div>
        <nav id={navId} aria-label="Main" className="min-h-0 flex-1 overflow-y-auto px-2">
          <ul className="flex flex-col gap-0.5">
            {links.map((l) => (
              <li key={l.to}>
                <NavLink to={l.to} end={l.end} className={({ isActive }) => `${item} ${collapsed ? "justify-center px-0" : ""} ${isActive ? "bg-canvas font-semibold text-ink" : "text-ink hover:bg-canvas"}`}>
                  <Icon name={l.icon} />
                  {label(l.label)}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="border-t border-line px-2 py-2">
          {status === "signed-in" && me ? (
            <>
              {/* The person is the way to their own account, open or collapsed — the name and the initials are one link
                  (owner, 2026-09-22). */}
              <NavLink to="/account" className={({ isActive }) => `${item} text-sm ${collapsed ? "justify-center px-0" : ""} ${isActive ? "bg-canvas font-semibold text-ink" : "text-muted hover:bg-canvas hover:text-ink"}`}>
                <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-full border border-line bg-canvas text-xs font-semibold text-ink">
                  {initials(me.person.displayName)}
                </span>
                <span data-testid="signed-in-as" className={collapsed ? "sr-only" : "min-w-0 truncate"}>
                  {me.person.displayName}
                </span>
                <span className="sr-only">, your account</span>
                {collapsed && <Tip text={`${me.person.displayName} — your account`} />}
              </NavLink>
              {/* A power symbol, not a door with an arrow: in the strip that arrow read as the expand chevron and cost
                  the owner a session (owner, 2026-09-22). */}
              <button type="button" onClick={() => void signOut()} className={`${item} mt-0.5 text-muted hover:bg-canvas hover:text-ink ${collapsed ? "justify-center px-0" : ""}`}>
                <Icon name="power" />
                {label("Sign out")}
              </button>
            </>
          ) : (
            status === "signed-out" && (
              <NavLink to="/login" className={`${item} text-ink hover:bg-canvas ${collapsed ? "justify-center px-0" : ""}`}>
                <Icon name="log-in" />
                {label("Sign in")}
              </NavLink>
            )
          )}
        </div>
      </header>
      <div className="flex min-h-dvh min-w-0 flex-col pl-14 md:pl-0">
        {signedIn && (
          // The only thing top right (ui-design.md §6): the person and Sign out are in the rail's foot. Its name is for a
          // screen reader and, on hover and on keyboard focus, a tooltip — the round ? is the whole of what is drawn.
          // Sticky, painting the canvas: it stays top right while the page scrolls under it, and the frozen page banner
          // sticks below it rather than behind it, so it covers none of the banner (gairm/lms#220).
          <aside ref={helpRow} aria-label="Help" data-testid="help-row" className="sticky top-0 z-20 mx-auto flex w-full max-w-7xl justify-end bg-canvas px-4 pt-2 lg:px-10">
            <button
              type="button"
              onClick={() => setSupportOpen(true)}
              aria-haspopup="dialog"
              aria-keyshortcuts="?"
              ref={helpButton}
              data-testid="support-button"
              // It sticks, so it is always in view; but it sits inside the page's scroll-padding (index.css), and focus
              // reaching it — Tab, or coming back from the panel — would scroll the page to "reveal" it. Its scroll area
              // is moved down by exactly that padding, so the browser sees it in view and leaves the page where it is.
              className="group relative flex size-11 items-center justify-center rounded-full border border-line bg-surface text-lg font-semibold text-ink [scroll-margin-bottom:calc(var(--shell-bar,0px)+var(--page-bar,0px)+0.5rem)] [scroll-margin-top:calc(-1*(var(--shell-bar,0px)+var(--page-bar,0px)+0.5rem))] hover:bg-canvas"
            >
              <span aria-hidden="true">?</span>
              <span className="sr-only">Help</span>
              <Tip text="Help (?)" side="below" />
            </button>
          </aside>
        )}
        {supportOpen && signedIn && <SupportPanel onClose={closeSupport} returnTo={helpButton} />}
        {status === "signed-in" && <NudgeBanner />}
        {/* --page-inset-x is main's side padding, which the frozen page banner bleeds into (PageHeader). */}
        <main id="main" tabIndex={-1} className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 text-sm [--page-inset-x:1rem] lg:px-10 lg:[--page-inset-x:2.5rem]">
          <Outlet />
          {/* Inside main so it is in a landmark; last so it is the last Tab stop of the page's content. */}
          <BackToTop />
        </main>
        <footer className="border-t border-line bg-surface">
          {/* The bottom padding keeps "What changed in" clear of Back to top, which floats over this corner. */}
          <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center justify-between gap-3 px-4 pt-4 pb-16 text-xs text-muted lg:px-10">
            <p>
              {productName} <span data-testid="version">{version}</span>
              {apiVersion && vtag(apiVersion) !== version && (
                // A stale cached bundle is visible instead of mysterious (release-notes template).
                <span data-testid="api-version"> (API {vtag(apiVersion)})</span>
              )}
              {nonProd && (
                <>
                  {" · "}
                  <span data-testid="environment">{config.envName}</span>
                </>
              )}
            </p>
            <NavLink to={`/docs/release-notes#${version}`} className="inline-flex min-h-11 items-center">
              What changed in {version}
            </NavLink>
          </div>
        </footer>
      </div>
    </div>
  );
}

/**
 * The name of a control whose own text is hidden, shown on hover and on keyboard
 * focus — the "every control has a visible label" rule, kept while the rail is a
 * strip of icons.
 *
 * Positioned `fixed` and measured from the control rather than `absolute` inside
 * it: the nav is a scroll container, a scroll container clips *both* axes, and so
 * the absolute tooltip this replaced was cut off at the strip's edge and never
 * appeared on a nav item at all (owner, 2026-09-22). Decorative to a screen
 * reader — the `sr-only` text beside it is the name.
 */
function Tip({ text, side = "right" }: { text: string; side?: "right" | "below" }) {
  const marker = useRef<HTMLSpanElement>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const shown = at !== null;
  const measure = useCallback((r: DOMRect) => (side === "below" ? { top: r.bottom + 8, left: r.right } : { top: r.top + r.height / 2, left: r.right + 8 }), [side]);

  useEffect(() => {
    const control = marker.current?.parentElement;
    if (!control) return;
    const place = () => setAt(measure(control.getBoundingClientRect()));
    const hide = () => setAt(null);
    // Focus shows it for keyboard users; a click that happens to focus the control should not.
    const onFocus = () => {
      try {
        if (!control.matches(":focus-visible")) return;
      } catch {
        // a browser without :focus-visible: show it on any focus rather than never
      }
      place();
    };
    control.addEventListener("pointerenter", place);
    control.addEventListener("pointerleave", hide);
    control.addEventListener("focus", onFocus);
    control.addEventListener("blur", hide);
    return () => {
      control.removeEventListener("pointerenter", place);
      control.removeEventListener("pointerleave", hide);
      control.removeEventListener("focus", onFocus);
      control.removeEventListener("blur", hide);
    };
  }, [measure]);

  // While it is up the strip can still scroll under it, and the window can change size.
  useEffect(() => {
    const control = marker.current?.parentElement;
    if (!shown || !control) return;
    const place = () => setAt(measure(control.getBoundingClientRect()));
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [shown, measure]);

  return (
    <span ref={marker} className="contents">
      {at && (
        <span aria-hidden="true" style={{ top: at.top, left: at.left }} className={`pointer-events-none fixed z-50 ${side === "below" ? "-translate-x-full" : "-translate-y-1/2"} whitespace-nowrap rounded-md border border-line bg-surface px-2 py-1 text-sm text-ink shadow-md`}>
          {text}
        </span>
      )}
    </span>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "")).toUpperCase() || "?";
}
